import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Science as ScienceIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Article as ArticleIcon,
  LockOpen as LockOpenIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import {
  researchProjectService,
  ndaService,
  buildResearchImageUrl,
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import ImageViewer from "../../components/ImageViewer";

const PublicResearchSearch = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [success, setSuccess] = useState("");
  const { isAuthenticated, user } = useAuth();
  const [filters, setFilters] = useState({
    search: "",
  });

  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signTargetProject, setSignTargetProject] = useState(null);

  // Track NDA private access per project id
  const [privateAccessMap, setPrivateAccessMap] = useState({});
  // Track NDA status per project
  const [ndaStatusMap, setNdaStatusMap] = useState({});

  // State para ImageViewer
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState("");
  const [selectedImageTitle, setSelectedImageTitle] = useState("");

  // State para flujo NDA upload
  const [ndaUploadFile, setNdaUploadFile] = useState(null);
  const [uploadingNda, setUploadingNda] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError("");

      const params = {};
      if (filters.search) params.search = filters.search;

      const response = await researchProjectService.search(params);
      const list = response.data || [];
      setProjects(list);

      // Probe backend access and NDA status per project to reflect true state in UI
      if (isAuthenticated() && Array.isArray(list) && list.length > 0) {
        const ndaProjects = list.filter((p) => p && p.requiresNDA);
        if (ndaProjects.length > 0) {
          // Fetch file access and NDA status in parallel
          const [fileResults, statusResults] = await Promise.all([
            Promise.allSettled(
              ndaProjects.map((p) => researchProjectService.getFiles(p.id))
            ),
            Promise.allSettled(
              ndaProjects.map((p) => researchProjectService.getNDAStatus(p.id))
            ),
          ]);

          const accessMapUpdate = {};
          fileResults.forEach((res, idx) => {
            if (res.status === "fulfilled") {
              const data = res.value?.data;
              if (data?.hasPrivateAccess === true) {
                const projectId = ndaProjects[idx].id;
                accessMapUpdate[projectId] = true;
              }
            }
          });
          if (Object.keys(accessMapUpdate).length > 0) {
            setPrivateAccessMap((prev) => ({ ...prev, ...accessMapUpdate }));
          }

          const statusMapUpdate = {};
          statusResults.forEach((res, idx) => {
            if (res.status === "fulfilled") {
              const data = res.value?.data;
              if (data) {
                const projectId = ndaProjects[idx].id;
                statusMapUpdate[projectId] = data;
                if (data.hasAccess) {
                  accessMapUpdate[projectId] = true;
                }
              }
            }
          });
          if (Object.keys(statusMapUpdate).length > 0) {
            setNdaStatusMap((prev) => ({ ...prev, ...statusMapUpdate }));
          }
          // Update access map again with NDA status info
          if (Object.keys(accessMapUpdate).length > 0) {
            setPrivateAccessMap((prev) => ({ ...prev, ...accessMapUpdate }));
          }
        }
      }
    } catch (err) {
      setError("Error al buscar proyectos de investigación");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
    });
    setTimeout(() => handleSearch(), 100);
  };

  const handleViewProject = async (project) => {
    try {
      if (!project.requiresNDA) {
        setSelectedProject(project);
        setOpenDialog(true);
        setSuccess("");
        setError("");
        return;
      }

      // Requires NDA
      if (!isAuthenticated()) {
        window.location.href = "/login";
        return;
      }

      // Fetch NDA status for this project
      try {
        const statusRes = await researchProjectService.getNDAStatus(project.id);
        const statusData = statusRes?.data;
        if (statusData) {
          setNdaStatusMap((prev) => ({ ...prev, [project.id]: statusData }));
          if (statusData.hasAccess) {
            setPrivateAccessMap((prev) => ({ ...prev, [project.id]: true }));
          }
        }
      } catch (e) {
        // ignore
      }

      // If owner or admin, allow viewing full details via getById
      try {
        const resp = await researchProjectService.getById(project.id);
        const full = resp.data;
        const isOwner = full?.userId && user?.id && full.userId === user.id;
        const isAdmin = user?.role === "admin";
        if (isOwner || isAdmin) {
          setSelectedProject(full);
          setOpenDialog(true);
          setSuccess("");
          setError("");
          return;
        }
      } catch (e) {
        // ignore; fall through for non-owner/admin
      }

      // For non-owner/admin: ask backend which files we can see
      try {
        const filesRes = await researchProjectService.getFiles(project.id);
        const files =
          filesRes?.data?.files ||
          filesRes?.data?.project?.files ||
          filesRes?.data?.files ||
          filesRes?.data?.files;
        if (filesRes?.data?.hasPrivateAccess === true) {
          setPrivateAccessMap((prev) => ({ ...prev, [project.id]: true }));
        }
        setSelectedProject({
          ...project,
          files: Array.isArray(files) ? files : project.files,
          hasPrivateAccess: filesRes?.data?.hasPrivateAccess === true,
        });
        setOpenDialog(true);
        setSuccess("");
        setError("");
        return;
      } catch (e) {
        // If even files endpoint is not accessible, fall back to sign dialog
      }

      // Not owner/admin and files not accessible: open sign simulation dialog
      openSignDialog(project);
      setSuccess("");
    } catch (err) {
      console.error(err);
      setError("No se pudo abrir el proyecto");
    }
  };

  // Open simulate sign dialog
  const openSignDialog = (project) => {
    if (!isAuthenticated()) {
      window.location.href = "/login";
      return;
    }
    setSignTargetProject(project);
    setSignDialogOpen(true);
  };

  const closeSignDialog = () => {
    setSignDialogOpen(false);
    setSignTargetProject(null);
  };

  // Confirm NDA access request: creates signature with pending_upload status
  const confirmSimulateSign = async () => {
    try {
      if (!signTargetProject) return;
      setError("");
      const response = await researchProjectService.signNDA(signTargetProject.id);
      const ndaStatus = response?.data?.ndaStatus;
      const signatureId = response?.data?.signatureId;

      // Update NDA status map with new data
      setNdaStatusMap((prev) => ({
        ...prev,
        [signTargetProject.id]: {
          ...prev[signTargetProject.id],
          signatureStatus: ndaStatus,
          signatureId: signatureId,
        },
      }));

      if (ndaStatus === "signed") {
        setPrivateAccessMap((prev) => ({
          ...prev,
          [signTargetProject.id]: true,
        }));
        setSelectedProject(signTargetProject);
        setOpenDialog(true);
        setSuccess("Ya tienes acceso a este proyecto.");
      } else if (ndaStatus === "pending_upload") {
        setSelectedProject(signTargetProject);
        setOpenDialog(true);
        setSuccess(
          "Solicitud creada. Descarga el NDA, completalo y subilo para continuar."
        );
      } else if (ndaStatus === "pending_legal") {
        setSelectedProject(signTargetProject);
        setOpenDialog(true);
        setSuccess(
          "Tu NDA fue enviado. Pendiente de aprobacion del estudio juridico."
        );
      } else {
        setSelectedProject(signTargetProject);
        setOpenDialog(true);
        setSuccess(
          response?.data?.message || "Solicitud procesada correctamente."
        );
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error ||
          "No se pudo procesar la solicitud de firma del NDA para este proyecto"
      );
    } finally {
      closeSignDialog();
    }
  };

  // Download NDA template
  const handleDownloadTemplate = async (signatureId) => {
    try {
      setDownloadingTemplate(true);
      setError("");
      const response = await ndaService.downloadTemplate(signatureId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      const fileName = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
        : "nda-template.docx";
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Error al descargar el NDA");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Upload completed NDA
  const handleUploadCompletedNda = async (signatureId, projectId) => {
    try {
      if (!ndaUploadFile) {
        setError("Selecciona un archivo para subir");
        return;
      }
      setUploadingNda(true);
      setError("");
      const formData = new FormData();
      formData.append("file", ndaUploadFile);
      const response = await ndaService.uploadCompletedNda(signatureId, formData);
      const txHash = response?.data?.blockchainTxHash;

      // Update status map
      setNdaStatusMap((prev) => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          signatureStatus: "pending_legal",
          signatureBlockchainTxHash: txHash,
        },
      }));

      setNdaUploadFile(null);
      setSuccess(
        txHash
          ? `NDA subido exitosamente y registrado en blockchain. El hash en blockchain es la firma legal. Tx: ${txHash}`
          : "NDA subido exitosamente. Pendiente de aprobacion legal."
      );
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Error al subir el NDA completado");
    } finally {
      setUploadingNda(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProject(null);
  };

  const handleRequestAccess = () => {
    if (!selectedProject) return;
    openSignDialog(selectedProject);
  };

  const hasPrivateAccessFor = (projectId) => !!privateAccessMap[projectId];

  // Determinar si el usuario realmente puede ver archivos privados (NDA firmado, admin o dueño)
  const canViewPrivateFiles = (project) => {
    if (!project) return false;
    if (user?.role === "admin") return true;
    if (project.userId && user?.id && project.userId === user.id) return true;
    const status = ndaStatusMap[project.id];
    return status?.signatureStatus === "signed";
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: "center", position: "relative" }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
          }}
          aria-label="Volver atrás"
        >
          <ArrowBackIcon />
        </IconButton>
        <Box
          component="img"
          src="/logo-unir-h.png"
          alt="UNIR - Vicerrectorado de Transferencia"
          sx={{
            maxWidth: { xs: "80%", sm: "60%", md: "500px" },
            height: "auto",
            mb: 3,
            mx: "auto",
            display: "block",
          }}
        />
        <Typography variant="h3" component="h1" gutterBottom>
          Base de Datos de Proyectos de Investigación
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Busca y explora proyectos de investigación públicos, patentes y
          propiedad intelectual
        </Typography>
      </Box>

      {/* Search Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros de Búsqueda
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Título del Proyecto o Palabras Clave"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Buscar por título, palabras clave..."
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button variant="outlined" onClick={handleClearFilters}>
                Limpiar Filtros
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
              >
                Buscar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      )}

      {/* Results */}
      {!loading && (
        <>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {projects.length} Proyecto{projects.length !== 1 ? "s" : ""}{" "}
              Encontrado{projects.length !== 1 ? "s" : ""}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} md={6} key={project.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" gutterBottom>
                      {project.title}
                    </Typography>

                    {/* Public files inline on the card */}
                    {project.files &&
                      Array.isArray(project.files) &&
                      project.files.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="subtitle2"
                            color="primary"
                            gutterBottom
                          >
                            Archivos públicos:
                          </Typography>
                          <List dense>
                            {project.files
                              .filter(
                                (f) =>
                                  !project.requiresNDA || f.isPublic === true
                              )
                              .map((file, idx) => (
                                <ListItem key={idx} sx={{ py: 0 }}>
                                  <ListItemIcon>
                                    <LockOpenIcon color="success" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {file.normalizedName ||
                                            file.originalName}
                                        </Typography>
                                        <Chip
                                          label="Público"
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                        {file.path && (
                                          <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => {
                                              const imageUrl =
                                                buildResearchImageUrl(
                                                  file.path
                                                );
                                              setSelectedImageSrc(imageUrl);
                                              setSelectedImageTitle(
                                                file.normalizedName ||
                                                  file.originalName
                                              );
                                              setImageViewerOpen(true);
                                            }}
                                          >
                                            Ver
                                          </Button>
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      file.originalName !== file.normalizedName
                                        ? file.originalName
                                        : null
                                    }
                                  />
                                </ListItem>
                              ))}
                          </List>
                        </Box>
                      )}

                    {/* Minimal meta info (no full details if NDA required) */}
                    <Box
                      sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}
                    >
                      {project.requiresNDA && (
                        <Chip
                          label="Requiere NDA"
                          color="warning"
                          size="small"
                        />
                      )}
                      {/* NDA status chip */}
                      {project.requiresNDA && ndaStatusMap[project.id]?.signatureStatus === "pending_upload" && (
                        <Chip label="Pendiente de carga" color="info" size="small" />
                      )}
                      {project.requiresNDA && ndaStatusMap[project.id]?.signatureStatus === "pending_legal" && (
                        <Chip label="Pendiente aprobación" color="warning" size="small" variant="outlined" />
                      )}
                      {project.requiresNDA && ndaStatusMap[project.id]?.signatureStatus === "signed" && (
                        <Chip label="Acceso concedido" color="success" size="small" />
                      )}
                      {project.requiresNDA && ndaStatusMap[project.id]?.signatureStatus === "rejected" && (
                        <Chip label="Rechazado" color="error" size="small" />
                      )}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    {(() => {
                      const isOwner =
                        isAuthenticated() && user && project.userId === user.id;
                      const isAdmin =
                        isAuthenticated() && user && user.role === "admin";
                      const hasAccess =
                        !project.requiresNDA ||
                        isOwner ||
                        isAdmin ||
                        hasPrivateAccessFor(project.id);
                      const label = hasAccess
                        ? "Ver detalles completos"
                        : "Ver detalles";
                      return (
                        <Button
                          size="small"
                          variant="contained"
                          color={hasAccess ? "success" : "primary"}
                          fullWidth
                          onClick={() => handleViewProject(project)}
                          startIcon={<LockOpenIcon />}
                        >
                          {label}
                        </Button>
                      );
                    })()}
                  </CardActions>
                </Card>
              </Grid>
            ))}

            {projects.length === 0 && !loading && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: "center" }}>
                  <ScienceIcon
                    sx={{ fontSize: 60, color: "action.disabled", mb: 2 }}
                  />
                  <Typography variant="h6" color="textSecondary">
                    No se encontraron proyectos
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    Intenta ajustar tus filtros de búsqueda
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* Project Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedProject?.title}</DialogTitle>
        <DialogContent>
          {selectedProject && (
            <Box sx={{ mt: 2 }}>
              {/* Acceso Badge */}
              <Box sx={{ mb: 3 }}>
                <Chip
                  label="Acceso Público"
                  color="success"
                  size="medium"
                  icon={<LockOpenIcon />}
                />
              </Box>

              {/* NDA Status Tracker */}
              {selectedProject.requiresNDA && isAuthenticated() && ndaStatusMap[selectedProject.id] && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Estado de acceso NDA:
                  </Typography>
                  {(() => {
                    const status = ndaStatusMap[selectedProject.id];
                    if (!status.signatureStatus) {
                      return <Chip label="Sin solicitar" size="small" sx={{ backgroundColor: "#e0e0e0" }} />;
                    }
                    if (status.signatureStatus === "pending_upload") {
                      return (
                        <Box>
                          <Chip label="Pendiente: descargue y suba el NDA completado" color="info" size="small" />
                          <Box sx={{ mt: 2, p: 2, backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              1. Descargue el documento NDA, completelo en su computadora, y luego subalo.
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleDownloadTemplate(status.signatureId)}
                                disabled={downloadingTemplate}
                              >
                                {downloadingTemplate ? "Descargando..." : "Descargar NDA"}
                              </Button>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              2. Suba el NDA completado:
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                              <input
                                accept=".pdf"
                                style={{ display: "none" }}
                                id="nda-upload-completed"
                                type="file"
                                onChange={(e) => setNdaUploadFile(e.target.files[0])}
                              />
                              <label htmlFor="nda-upload-completed">
                                <Button variant="outlined" component="span" size="small">
                                  {ndaUploadFile ? ndaUploadFile.name : "Seleccionar archivo"}
                                </Button>
                              </label>
                              <Button
                                variant="contained"
                                size="small"
                                color="warning"
                                onClick={() => handleUploadCompletedNda(status.signatureId, selectedProject.id)}
                                disabled={!ndaUploadFile || uploadingNda}
                              >
                                {uploadingNda ? "Subiendo..." : "Subir NDA completado"}
                              </Button>
                            </Box>
                            <Alert severity="info" sx={{ mt: 2 }}>
                              Al subir el documento, se registrara en blockchain. El hash en blockchain es la firma legal.
                            </Alert>
                          </Box>
                        </Box>
                      );
                    }
                    if (status.signatureStatus === "pending_legal") {
                      return (
                        <Box>
                          <Chip label="NDA enviado - Pendiente aprobacion legal" color="warning" size="small" />
                          {status.signatureBlockchainTxHash && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="textSecondary">
                                Hash blockchain (firma legal):
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.75rem",
                                  wordBreak: "break-all",
                                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                                  padding: 0.5,
                                  borderRadius: 1,
                                }}
                              >
                                {status.signatureBlockchainTxHash}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    }
                    if (status.signatureStatus === "signed") {
                      return <Chip label="Firmado - Acceso concedido" color="success" size="small" />;
                    }
                    if (status.signatureStatus === "rejected") {
                      return (
                        <Box>
                          <Chip label="Rechazado por estudio juridico" color="error" size="small" />
                          {status.legalComments && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                              Motivo: {status.legalComments}
                            </Typography>
                          )}
                        </Box>
                      );
                    }
                    return <Chip label={status.signatureStatus} size="small" />;
                  })()}
                </Box>
              )}

              {/* Description */}
              {selectedProject.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Descripción:
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedProject.description}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                {/* Keywords */}
                {selectedProject.keywords && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary">
                      Palabras Clave:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <ArticleIcon fontSize="small" color="action" />
                      <Typography variant="body1">
                        {Array.isArray(selectedProject.keywords)
                          ? selectedProject.keywords.join(", ")
                          : String(selectedProject.keywords)}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Files */}
                {selectedProject.files &&
                  Array.isArray(selectedProject.files) &&
                  selectedProject.files.length > 0 && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        gutterBottom
                      >
                        {selectedProject.requiresNDA &&
                        !hasPrivateAccessFor(selectedProject.id)
                          ? "Archivos Públicos Disponibles:"
                          : "Archivos Disponibles:"}
                      </Typography>
                      <List dense>
                        {selectedProject.files
                          .filter(
                            (f) =>
                              !selectedProject.requiresNDA ||
                              hasPrivateAccessFor(selectedProject.id) ||
                              f.isPublic === true
                          )
                          .map((file, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <LockOpenIcon color="success" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {file.normalizedName || file.originalName}
                                    </Typography>
                                    <Chip
                                      label={
                                        selectedProject.requiresNDA &&
                                        !file.isPublic
                                          ? "Privado (con acceso)"
                                          : "Público"
                                      }
                                      size="small"
                                      color={
                                        selectedProject.requiresNDA &&
                                        !file.isPublic
                                          ? "warning"
                                          : "success"
                                      }
                                      variant="outlined"
                                    />
                                    {file.path && (
                                      <>
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() => {
                                            const imageUrl =
                                              buildResearchImageUrl(file.path);
                                            setSelectedImageSrc(imageUrl);
                                            setSelectedImageTitle(
                                              file.normalizedName ||
                                                file.originalName
                                            );
                                            setImageViewerOpen(true);
                                          }}
                                        >
                                          Ver
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="text"
                                          startIcon={<DownloadIcon />}
                                          onClick={() => {
                                            const fileUrl = buildResearchImageUrl(file.path);
                                            const link = document.createElement("a");
                                            link.href = fileUrl;
                                            link.setAttribute("download", file.normalizedName || file.originalName || "archivo");
                                            document.body.appendChild(link);
                                            link.click();
                                            link.remove();
                                          }}
                                        >
                                          Descargar
                                        </Button>
                                      </>
                                    )}
                                  </Box>
                                }
                                secondary={
                                  file.originalName !== file.normalizedName
                                    ? file.originalName
                                    : null
                                }
                              />
                            </ListItem>
                          ))}
                      </List>
                      {selectedProject.requiresNDA &&
                        !hasPrivateAccessFor(selectedProject.id) && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              Este proyecto contiene archivos privados
                              adicionales que requieren NDA. Solicita acceso
                              para verlos.
                            </Typography>
                          </Alert>
                        )}
                    </Grid>
                  )}

                {/* Dates */}
                {(selectedProject.startDate || selectedProject.endDate) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary">
                      Período:
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {selectedProject.startDate &&
                        `Inicio: ${new Date(
                          selectedProject.startDate
                        ).toLocaleDateString()}`}
                      {selectedProject.startDate &&
                        selectedProject.endDate &&
                        " | "}
                      {selectedProject.endDate &&
                        `Fin: ${new Date(
                          selectedProject.endDate
                        ).toLocaleDateString()}`}
                    </Typography>
                  </Grid>
                )}

                {/* Publication Date */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Publicado:{" "}
                    {new Date(selectedProject.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>

              {/* Messages */}
              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {success}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedProject?.requiresNDA &&
            isAuthenticated() &&
            !hasPrivateAccessFor(selectedProject.id) &&
            (() => {
              const status = ndaStatusMap[selectedProject.id];
              const sigStatus = status?.signatureStatus;
              // Hide button if there's an active request (pending_upload, pending_legal, signed)
              if (sigStatus === "pending_upload" || sigStatus === "pending_legal" || sigStatus === "signed") {
                return null;
              }
              return (
                <Button
                  onClick={handleRequestAccess}
                  variant="contained"
                  color="warning"
                >
                  {sigStatus === "rejected" ? "Re-solicitar acceso (NDA)" : "Solicitar acceso (NDA)"}
                </Button>
              );
            })()}
          <Button onClick={handleCloseDialog} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* NDA Signing Dialog */}
      <Dialog
        open={signDialogOpen}
        onClose={closeSignDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Solicitar acceso (NDA)</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Para acceder a los archivos privados de este proyecto, debe completar el proceso de NDA.
          </Alert>
          <Typography variant="body1" paragraph>
            Al confirmar, se creará su solicitud de acceso. Luego deberá:
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            1. Descargar el documento NDA del proyecto.<br />
            2. Completar todos los datos que se solicitan.<br />
            3. Convertirlo a formato PDF y firmarlo con firma digital.<br />
            4. Subirlo al sistema (se registrará en la blockchain).<br />
            5. Esperar la aprobación de Asesoría Jurídica.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            El hash en la blockchain del documento subido servirá como firma legal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSignDialog} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={confirmSimulateSign}
            variant="contained"
            color="warning"
          >
            Iniciar solicitud de acceso
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageSrc={selectedImageSrc}
        imageTitle={selectedImageTitle}
      />
    </Container>
  );
};

export default PublicResearchSearch;
